/**
 * Prevent double-submit / double-click from firing the same action twice.
 * Uses a sync lock (refs survive before React re-renders `disabled`).
 */
import { useCallback, useRef, useState } from "react";

/**
 * @param {(...args: any[]) => any | Promise<any>} action
 * @param {{ cooldownMs?: number, onDuplicate?: () => void }} [options]
 * @returns {[(...args: any[]) => Promise<{ ignored: boolean, result?: any, error?: any }>, boolean]}
 */
export function useExclusiveAction(action, { cooldownMs = 600, onDuplicate } = {}) {
  const lockRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async (...args) => {
    if (lockRef.current) {
      onDuplicate?.();
      return { ignored: true };
    }
    lockRef.current = true;
    setBusy(true);
    try {
      const result = await action(...args);
      return { ignored: false, result };
    } catch (error) {
      return { ignored: false, error };
    } finally {
      window.setTimeout(() => {
        lockRef.current = false;
        setBusy(false);
      }, cooldownMs);
    }
  }, [action, cooldownMs, onDuplicate]);

  return [run, busy];
}
